/**
 * CartConflict — pure cart-conflict detection (god-object refactor — step 4).
 *
 * Extracted verbatim from script.js. Standalone: no DOM, no app state, NO imports. The classifier
 * dependency (getProductType) stays in script.js — findConflictingItems receives cart items whose
 * productType is pre-computed by the caller (addToCartWithConflictCheck), so this module never
 * touches plan classification.
 *
 * Exposed as window.WoxomQuote.CartConflict; script.js aliases conflictGroupOf + findConflictingItems
 * back to writable window globals (both are relied on directly by tests/e2e/cart-conflict.spec.js,
 * and addToCartWithConflictCheck — which stays in script.js — calls findConflictingItems).
 *
 * CJS twin: netlify/functions/shared/package-rules.js ports this module's rule SEMANTICS
 * server-side for quotesheet package-composition validation (keys on quotesheet product fields
 * instead of live cart plan objects — see that file's header for the mapping notes).
 */

// Cart conflict groups (PR C). Plans whose product type shares a group are mutually
// exclusive in the cart; free-stack types (wellness, gap, additional) never conflict.
export const CONFLICT_GROUPS = {
  'primary-health': ['aca', 'tri-term', 'stm', 'major-medical', 'limited-medical'],
  'dental': ['dental'],
  'vision': ['vision'],
  'accident': ['accident'],
  'critical-illness': ['critical-illness'],
  'specified-disease': ['specified-disease'],
  'hospital-indemnity': ['hospital-indemnity'],
  'term-life': ['term-life'],
};

export function conflictGroupOf(productType) {
  for (var g in CONFLICT_GROUPS) {
    if (CONFLICT_GROUPS.hasOwnProperty(g) && CONFLICT_GROUPS[g].indexOf(productType) !== -1) return g;
  }
  return null; // free-stack
}

// cartItems: [{groupKey, name, productType}]. Returns the items that conflict with
// adding newType at newGroupKey — same conflict-group, different group key.
export function findConflictingItems(cartItems, newType, newGroupKey) {
  var g = conflictGroupOf(newType);
  if (!g) return [];
  var nk = newGroupKey != null ? String(newGroupKey) : null;
  return (cartItems || []).filter(function(it) {
    return String(it.groupKey) !== nk && conflictGroupOf(it.productType) === g;
  });
}

// AHS standalone-sale enforcement (carrier feedback 2026-08-10): Recuro and Specified Care
// cannot be sold without an Allstate Health STM or Fixed-Benefit Limited Medical base plan in
// the cart. api.js stamps the two planDetails flags; these predicates read them — hard gate at
// add-time (script.js addToCartWithConflictCheck) and a Cart-subscriber sweep that evicts any
// dependent left orphaned when its base plan is later removed.
export function requiresAhsBase(plan) {
  return !!(plan && plan.planDetails && plan.planDetails.requiresAhsBase);
}
export function isAhsBase(plan) {
  return !!(plan && plan.planDetails && plan.planDetails.ahsBase);
}
export function hasAhsBase(cartPlans) {
  return (cartPlans || []).some(isAhsBase);
}
export function findAhsOrphans(cartItems) {
  var plans = (cartItems || []).map(function(it) { return it && it.plan; });
  if (hasAhsBase(plans)) return [];
  return (cartItems || []).filter(function(it) { return requiresAhsBase(it && it.plan); });
}

// Any-companion enforcement (owner clarification 2026-08-11): Select Dental "Copay Plan 2" sells
// with ANY other policy — it just can't be sold alone. It is NOT an AHS dependent (no longer
// stamps requiresAhsBase) and never pairs with another dental, which CONFLICT_GROUPS['dental']
// already enforces via the swap modal, so no work is needed for that half of the rule.
export function requiresCompanion(plan) {
  return !!(plan && plan.planDetails && plan.planDetails.requiresCompanion);
}
// Any-companion orphans: items needing at least one OTHER policy, when every cart item carries
// the requirement (i.e. nothing else is present to satisfy it).
export function findCompanionOrphans(cartItems) {
  var items = cartItems || [];
  if (!items.length) return [];
  if (items.some(function(it) { return !requiresCompanion(it && it.plan); })) return [];
  return items.filter(function(it) { return requiresCompanion(it && it.plan); });
}

// Restore-order fix (final review, 2026-08-10; generalized 2026-08-11 for the companion rule).
// Saved-quote restore re-adds plans one-by-one via Cart.add, each of which notifies synchronously
// — same sweep-ordering invariant as the conflict-modal add-then-remove fix. If a dependent
// (Recuro/Specified Care, OR a companion-requiring item like Copay Plan 2) lands before whatever
// satisfies it in the iterated list, the sweep observes an unsatisfied cart mid-restore and
// evicts it. Stable partition — everything that ISN'T a dependent first, then everything that
// requires an AHS base OR a companion, both in original relative order — guarantees a
// satisfying item is always present (when one exists in the input) before any dependent is added.
export function dependentsLast(plans) {
  var list = plans || [];
  var independents = [];
  var dependents = [];
  list.forEach(function(p) {
    ((requiresAhsBase(p) || requiresCompanion(p)) ? dependents : independents).push(p);
  });
  return independents.concat(dependents);
}

export const CartConflict = {
  CONFLICT_GROUPS, conflictGroupOf, findConflictingItems,
  requiresAhsBase, isAhsBase, hasAhsBase, findAhsOrphans,
  requiresCompanion, findCompanionOrphans, dependentsLast,
};
